import { Colors, DotPlot, Heading, SelectOutlined, Text } from '@amelio/ui-library';
import { useState } from 'react';

import { NotRepresentativePill } from './NotRepresentativePill';
import { SCORE_MAX, SCORE_MIN } from '../calibrationConstants';
import {
    CARD_SECTION,
    CHART_INTRO,
    DIFFERENTIATION_HEADER_LAYOUT,
    SORT_SELECT_WIDTH,
} from '../calibrationStyles';
import { formatScore } from '../utils/formatScore';

import type { CalibrationManager, CalibrationReport } from '@/types/calibration';
import type { DotPlotRow, DotPlotScale, OptionProps } from '@amelio/ui-library';

import { useAmelioTranslations } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { getLocalizedText } from '@/utils/localization';
import { getFullName } from '@/utils/user';

export interface ManagerDifferentiationViewProps {
    report: CalibrationReport;
    language: string;
}

type SortKey = 'scoreDesc' | 'scoreAsc' | 'spreadDesc' | 'spreadAsc' | 'name';

const DEFAULT_SORT: SortKey = 'scoreDesc';

const COMPARATORS: Record<SortKey, (a: CalibrationManager, b: CalibrationManager) => number> = {
    scoreDesc: (a, b) => b.averageScore - a.averageScore,
    scoreAsc: (a, b) => a.averageScore - b.averageScore,
    spreadDesc: (a, b) => b.spread - a.spread,
    spreadAsc: (a, b) => a.spread - b.spread,
    name: (a, b) => getFullName(a).localeCompare(getFullName(b)),
};

/** "Differentiation" pivot: one dot-plot track per manager, scattering their team's scores across
 * the company's performance-level bands. The bands stay unfilled — they are graduations for
 * reading a dot's position, not a second colour scale competing with the dots themselves. */
export const ManagerDifferentiationView = ({
    report,
    language,
}: ManagerDifferentiationViewProps) => {
    const { t } = useAmelioTranslations();
    const { getManagerLabel } = useCompanyInformation();
    const [sortKey, setSortKey] = useState<SortKey>(DEFAULT_SORT);

    const formatScoreValue = (value: number) => formatScore({ value, language });

    const sortedLevels = [...report.performanceLevels].sort((a, b) => a.minScore - b.minScore);
    const scale: DotPlotScale = {
        min: SCORE_MIN,
        max: SCORE_MAX,
        bands: sortedLevels.map((level, index) => ({
            from: level.minScore,
            to: sortedLevels[index + 1]?.minScore ?? SCORE_MAX,
            label: getLocalizedText(level.label),
        })),
    };

    const rows: DotPlotRow[] = [...report.managers].sort(COMPARATORS[sortKey]).map((manager) => {
        const scores = manager.employees.map((employee) => employee.score);

        return {
            id: manager.managerId,
            label: getFullName(manager),
            secondaryLabel: t('calibration.deviation.evaluatedCount', {
                count: manager.headcount,
            }),
            badge: manager.isRepresentative ? undefined : (
                <NotRepresentativePill
                    minimumHeadcount={report.minimumRepresentativeHeadcount}
                    dataTestId={`calibration-not-representative-${manager.managerId}`}
                />
            ),
            marker: manager.averageScore,
            formattedValue: formatScoreValue(manager.averageScore),
            pointCountLabel: t('calibration.differentiation.spreadLabel', {
                spread: formatScoreValue(manager.spread),
            }),
            formattedRange:
                scores.length > 0
                    ? t('calibration.differentiation.rangeLabel', {
                          min: formatScoreValue(Math.min(...scores)),
                          max: formatScoreValue(Math.max(...scores)),
                      })
                    : undefined,
            points: manager.employees.map((employee) => ({
                id: employee.employeeId,
                value: employee.score,
                label: `${getFullName(employee)} — ${formatScoreValue(employee.score)}`,
            })),
        };
    });

    const sortOptions: OptionProps[] = [
        { value: 'scoreDesc', label: t('calibration.differentiation.sortByScoreDesc') },
        { value: 'scoreAsc', label: t('calibration.differentiation.sortByScoreAsc') },
        { value: 'spreadDesc', label: t('calibration.differentiation.sortBySpreadDesc') },
        { value: 'spreadAsc', label: t('calibration.differentiation.sortBySpreadAsc') },
        { value: 'name', label: t('calibration.differentiation.sortByName') },
    ];

    return (
        <div className={CARD_SECTION}>
            <div className={DIFFERENTIATION_HEADER_LAYOUT}>
                <div className={`${CHART_INTRO} flex flex-col gap-1`}>
                    <Heading
                        level={4}
                        as={'h2'}
                        weight={'bold'}
                        color={Colors.NAVY}
                    >
                        {t('calibration.differentiation.title')}
                    </Heading>

                    <Text color={Colors.BASE_500}>{t('calibration.differentiation.intro')}</Text>
                </div>

                <div className={SORT_SELECT_WIDTH}>
                    <SelectOutlined
                        options={sortOptions}
                        value={sortKey}
                        onChange={(value) => setSortKey(value as SortKey)}
                        sort={'none'}
                        ariaLabel={t('calibration.differentiation.sortLabel')}
                        dataTestId={'calibration-differentiation-sort'}
                    />
                </div>
            </div>

            <DotPlot
                rows={rows}
                scale={scale}
                pointLabel={t('calibration.differentiation.pointLabel')}
                markerLabel={t('calibration.differentiation.markerLabel')}
                ariaLabel={t('calibration.differentiation.ariaLabel', {
                    managerLabel: getManagerLabel(),
                })}
                chevronPlacement={'start'}
                dataTestId={'calibration-differentiation-chart'}
            />
        </div>
    );
};
